import { Injectable } from "@angular/core";
import { Effect, ofType, Actions, createEffect } from "@ngrx/effects";
import { switchMap, catchError, mergeMap, exhaustMap } from "rxjs/operators";
import { of } from "rxjs";
import {
    EConsumptionActions,
    GetConsumptions,
    SetConsumptions,
    ConsumptionsAPIError,
    GetConsumptionsChartData,
    SetConsumptionsChartData,
    GetConsumptionsPeriod,
    SetConsumptionsPeriod,
    GetConsumptionEvolution,
    GetConsumptionHistory,
    GetMobieCardList,
    GetMobieConsumptions,
} from "../actions/consumption.actions";
import { DashboardService } from "@edp-services/dashboard.service";
import { IConsumptions } from "@edp-components/edp-card-widgets/edp-widget-conta-certa/edp-conta-certa.model";
import { ConsumptionsService } from "@edp-services/consumptions.service";
import { IConsumptionsChartData, IConsumptionsPeriod } from "@edp-models/consumptions.model";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { GetMobieConsumptionsPayload, SmartNowConsumptionEvolution } from "@edp-models/smart-now.model";

@Injectable()
export class ConsumptionEffects {
    @Effect()
    loadConsumptions$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetConsumptions),
        switchMap((action: GetConsumptions) =>
            this.dashboardService.getConsumptions(action.contract.ContractId).pipe(
                // switch to error action
                catchError((error: Error) => of(new ConsumptionsAPIError(error)))
            )
        ),
        switchMap((consumptions: IConsumptions | ConsumptionsAPIError) => {
            if (consumptions instanceof ConsumptionsAPIError) {
                return of(consumptions);
            } else {
                return of(new SetConsumptions(consumptions));
            }
        })
    );

    loadConsumptionChartData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EConsumptionActions.GetConsumptionsChartData),
            switchMap((action: GetConsumptionsChartData) => {
                this.store.dispatch(new ShowLoader(true));
                return this.consumptionsService
                    .getConsumptionsChartData(action.payload)
                    .pipe(catchError((error: Error) => of(new ConsumptionsAPIError(error))));
            }),
            switchMap((chartData: IConsumptionsChartData[] | ConsumptionsAPIError) => {
                this.store.dispatch(new ShowLoader(false));
                if (chartData instanceof ConsumptionsAPIError) {
                    return of(chartData);
                } else {
                    return of(new SetConsumptionsChartData(chartData));
                }
            })
        )
    );

    loadConsumptionPeriod$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EConsumptionActions.GetConsumptionsPeriod),
            mergeMap((action: GetConsumptionsPeriod) => {
                return this.consumptionsService
                    .getConsumptionPeriod(action.payload)
                    .pipe(catchError((error: Error) => of(new ConsumptionsAPIError(error))));
            }),
            mergeMap((period: IConsumptionsPeriod | ConsumptionsAPIError) => {
                if (period instanceof ConsumptionsAPIError) {
                    return of(period);
                } else {
                    return of(new SetConsumptionsPeriod(period));
                }
            })
        )
    );

    @Effect()
    getConsumptionEvolution$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetConsumptionEvolution),
        exhaustMap((action: GetConsumptionEvolution) => {
            this.store.dispatch(new ShowLoader(true));
            return this.consumptionsService.getConsumptionEvolution(action.payload).pipe(
                switchMap((consumpttion: SmartNowConsumptionEvolution) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetConsumptionEvolution, payload: consumpttion },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetConsumptionEvolution, payload: null },
                ])
            );
        })
    );

    @Effect()
    getConsumptionHistory$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetConsumptionHistory),
        exhaustMap((action: GetConsumptionHistory) => {
            this.store.dispatch(new ShowLoader(true));
            return this.consumptionsService.getConsumptionHistory(action.payload).pipe(
                switchMap((consumptionHistory: any) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetConsumptionHistory, payload: consumptionHistory },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getMobieConsumptionHistory$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetMobieConsumptionHistory),
        exhaustMap((action: GetConsumptionHistory) => {
            this.store.dispatch(new ShowLoader(true));
            return this.consumptionsService.getMobieConsumptionHistory(action.payload).pipe(
                switchMap((consumptionHistory: any) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetMobieConsumptionHistory, payload: consumptionHistory },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getMobieCardList$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetMobieCardList),
        exhaustMap((action: GetMobieCardList) => {
            this.store.dispatch(new ShowLoader(true));
            return this.consumptionsService.getMobieCardList(action.payload).pipe(
                switchMap((cards: string[]) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetMobieCardList, payload: cards },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetMobieCardList, payload: null },
                ])
            );
        })
    );

    @Effect()
    getMobieConsumptions$ = this.actions$.pipe(
        ofType(EConsumptionActions.GetMobieConsumptions),
        exhaustMap((action: GetMobieConsumptions) => {
            this.store.dispatch(new ShowLoader(true));
            return this.consumptionsService.getMobieConsumptions(action.payload).pipe(
                switchMap((consumptions: SmartNowConsumptionEvolution) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetMobieConsumptions, payload: consumptions },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EConsumptionActions.SetMobieConsumptions, payload: null },
                ])
            );
        })
    );

    constructor(
        private actions$: Actions,
        private dashboardService: DashboardService,
        private consumptionsService: ConsumptionsService,
        private store: Store<IAppState>
    ) {}
}
